import styled from "styled-components";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_CSS, FONT_STYLE } from "../../constants/design/fontSize";
import { COMPONENTS, SPACING } from "../../constants/design/spacing";

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${COMPONENTS.header.main}px);
  padding: ${SPACING.three}px;
  box-sizing: border-box;
  gap: ${SPACING.two}px;
  overflow: auto;
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${SPACING.three}px;
`;

export const PageTitle = styled.h2`
  ${FONT_CSS.header.five}
  color: ${TEXT_COLORS.one};
  margin: 0;
`;

export const PageSubtitle = styled.div`
  ${FONT_STYLE.label.two}
  color: ${TEXT_COLORS.two};
  margin-top: ${SPACING.one}px;
`;

export const FiltersWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${SPACING.two}px;
  padding: ${SPACING.two}px ${SPACING.three}px;
  border: 1px solid ${BORDER_COLORS.two};
  border-radius: 8px;
  background: ${BACKGROUND_COLORS.component};
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.one}px;
  ${FONT_STYLE.label.three}
  color: ${TEXT_COLORS.three};
  text-transform: uppercase;
`;

export const Input = styled.input`
  ${FONT_STYLE.label.one}
  height: 34px;
  min-width: 160px;
  padding: 0 ${SPACING.two}px;
  color: ${TEXT_COLORS.one};
  background: ${BACKGROUND_COLORS.four};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: ${COLORS.blue.one};
  }
`;

export const Select = styled.select`
  ${FONT_STYLE.label.one}
  height: 36px;
  min-width: 140px;
  padding: 0 ${SPACING.two}px;
  color: ${TEXT_COLORS.one};
  background: ${BACKGROUND_COLORS.four};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: ${COLORS.blue.one};
  }
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.two}px;
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.two}px;
`;

export const OverviewHeader = styled(SectionHeader)`
  align-items: flex-start;
  flex-wrap: wrap;
`;

export const SectionTitle = styled.div`
  ${FONT_STYLE.header.seven}
  color: ${TEXT_COLORS.one};
`;

export const SectionTitleStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.one}px;
`;

export const MutedText = styled.span`
  ${FONT_STYLE.label.two}
  color: ${TEXT_COLORS.two};
`;

export const ErrorText = styled.div`
  ${FONT_STYLE.label.two}
  color: ${COLORS.red.one};
`;

export const EmptyState = styled.div`
  ${FONT_STYLE.label.one}
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 ${SPACING.three}px;
  color: ${TEXT_COLORS.two};
  border: 1px solid ${BORDER_COLORS.two};
  border-radius: 8px;
  background: ${BACKGROUND_COLORS.four};
`;

export const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;

export const CellSubtext = styled.span`
  ${FONT_STYLE.label.three}
  color: ${TEXT_COLORS.three};
  text-transform: none;
`;

export const StatusBadge = styled.span<{ status?: string }>`
  ${FONT_STYLE.label.three}
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 76px;
  padding: 2px ${SPACING.two}px;
  border-radius: 6px;
  border: 1px solid
    ${({ status }) => {
      switch (status) {
        case "succeeded":
        case "filled":
          return COLORS.green.one;
        case "failed":
        case "cancelled":
          return COLORS.red.one;
        case "pending":
        case "reserved":
          return COLORS.orange.one;
        default:
          return BORDER_COLORS.one;
      }
    }};
  color: ${({ status }) => {
    switch (status) {
      case "succeeded":
      case "filled":
        return COLORS.green.one;
      case "failed":
      case "cancelled":
        return COLORS.red.one;
      case "pending":
      case "reserved":
        return COLORS.orange.one;
      default:
        return TEXT_COLORS.two;
    }
  }};
  background: ${BACKGROUND_COLORS.four};
  text-transform: capitalize;
`;

export const ActionRow = styled.div`
  display: flex;
  gap: ${SPACING.two}px;
  align-items: center;
`;

export const OverviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.three}px;
`;

export const HealthChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${SPACING.two}px;
`;

export const InventoryColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: ${SPACING.three}px;

  @media screen and (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const InventoryPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 1px solid ${BORDER_COLORS.two};
  border-radius: 8px;
  background: ${BACKGROUND_COLORS.component};

  > ${ErrorText} {
    margin: ${SPACING.twoHalf}px ${SPACING.three}px 0;
  }
`;

export const InventoryPanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.three}px;
  min-height: 64px;
  padding: ${SPACING.three}px;
  border-bottom: 1px solid ${BORDER_COLORS.two};

  @media screen and (max-width: 520px) {
    flex-direction: column;
  }
`;

export const NetworkIdentity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.twoHalf}px;
  min-width: 0;
`;

export const NetworkMark = styled.div`
  ${FONT_CSS.header.eight}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: ${TEXT_COLORS.one};
  background: ${BACKGROUND_COLORS.two};
  flex: 0 0 auto;
`;

export const NetworkName = styled.div`
  ${FONT_CSS.header.seven}
  color: ${TEXT_COLORS.one};
`;

export const NetworkDescription = styled.div`
  ${FONT_STYLE.label.two}
  color: ${TEXT_COLORS.two};
  margin-top: ${SPACING.one}px;
`;

export const AccountMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${SPACING.one}px;
  min-width: 0;

  @media screen and (max-width: 520px) {
    align-items: flex-start;
  }
`;

export const InventoryListHeader = styled.div`
  ${FONT_STYLE.label.three}
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(120px, auto);
  gap: ${SPACING.three}px;
  padding: ${SPACING.two}px ${SPACING.three}px;
  color: ${TEXT_COLORS.three};
  text-transform: uppercase;

  > :last-child {
    text-align: right;
  }
`;

export const InventoryList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const InventoryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(120px, auto);
  align-items: center;
  gap: ${SPACING.three}px;
  min-height: 56px;
  padding: ${SPACING.twoHalf}px ${SPACING.three}px;
  border-top: 1px solid ${BORDER_COLORS.three};
`;

export const InventoryAsset = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.twoHalf}px;
  min-width: 0;
`;

export const AssetLogo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
`;

export const AssetLogoFallback = styled.span`
  ${FONT_STYLE.label.three}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: ${TEXT_COLORS.one};
  background: ${BACKGROUND_COLORS.three};
  flex: 0 0 auto;
`;

export const AssetDetails = styled.div`
  min-width: 0;
`;

export const AssetName = styled.div`
  ${FONT_STYLE.label.one}
  color: ${TEXT_COLORS.one};
  overflow-wrap: anywhere;
`;

export const AssetMeta = styled.div`
  ${FONT_STYLE.label.three}
  color: ${TEXT_COLORS.three};
  margin-top: 2px;
  overflow-wrap: anywhere;
`;

export const InventoryBalance = styled.div`
  ${FONT_CSS.header.seven}
  color: ${TEXT_COLORS.one};
  text-align: right;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${SPACING.two}px;
`;

export const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
`;

export const PaginationSelect = styled.select`
  ${FONT_STYLE.label.two}
  height: 28px;
  padding: 0 ${SPACING.two}px;
  color: ${TEXT_COLORS.one};
  background: ${BACKGROUND_COLORS.four};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: ${COLORS.blue.one};
  }
`;

export const PaginationButton = styled.button`
  ${FONT_STYLE.label.two}
  min-width: 52px;
  height: 28px;
  padding: 0 ${SPACING.two}px;
  color: ${TEXT_COLORS.one};
  background: ${BACKGROUND_COLORS.four};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 6px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${COLORS.blue.one};
  }

  &:disabled {
    color: ${TEXT_COLORS.four};
    cursor: not-allowed;
  }
`;

export const PausedAssetsBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.two}px;
  padding: ${SPACING.three}px;
  border: 1px solid ${COLORS.orange.one};
  border-radius: 8px;
  background: ${COLORS.orange.five};
`;

export const PausedAssetsTitle = styled.div`
  ${FONT_STYLE.header.seven}
  color: ${COLORS.orange.one};
`;

export const PausedAssetsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.two}px;
`;

export const PausedAssetRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.two}px;
`;

export const PausedAssetText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.one}px;
  min-width: 0;
`;

export const PausedAssetName = styled.div`
  ${FONT_STYLE.label.one}
  color: ${TEXT_COLORS.one};
  overflow-wrap: anywhere;
`;

export const PausedAssetReason = styled.div`
  ${FONT_STYLE.label.two}
  color: ${TEXT_COLORS.two};
  overflow-wrap: anywhere;
`;
